import LegalLayout from "./LegalLayout";
import { AlertCircle, ShieldAlert, CheckCircle2, UserCheck, Flame } from "lucide-react";

export default function AcceptableUse() {
  return (
    <LegalLayout
      title="Acceptable Use & AI Safety Policy"
      subtitle="Guidelines for responsible creation, platform standards, and ethical artificial intelligence usage."
      lastUpdated="September 10, 2026"
    >
      <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">
        {/* Intro */}
        <div>
          <p className="text-base text-foreground font-medium">
            Virality AI is built to empower creators to find their authentic audience and produce high-impact content. To protect the creative ecosystem, our infrastructure, and end audiences, all users must adhere to this Acceptable Use and AI Safety Policy.
          </p>
          <p className="mt-2 text-xs">
            Failure to comply with these rules may result in immediate suspension or permanent termination of your account without refund.
          </p>
        </div>

        {/* Section 1 */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-red-500" /> 1. Prohibited Uses &amp; Malicious Content
          </h2>
          <p className="text-xs">
            You agree NOT to use Virality AI to generate, brainstorm, or assist in the creation of content that:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-xs">
            <li><strong>Harassment &amp; Hate Speech:</strong> Attacks, degrades, intimidates, or incites violence against individuals or groups based on race, ethnicity, religion, disability, age, nationality, sexual orientation, or gender identity.</li>
            <li><strong>Deceptive Impersonation &amp; Fraud:</strong> Impersonates living individuals, brands, or public agencies with intent to scam, defraud, or harvest personal data.</li>
            <li><strong>Illegal Activities:</strong> Promotes, instructs, or assists in financial fraud, cyberattacks, weapon manufacturing, or distribution of controlled substances.</li>
            <li><strong>Self-Harm &amp; Exploitation:</strong> Depicts, facilitates, or encourages suicide, self-harm, or child exploitation in any form.</li>
            <li><strong>Malware &amp; Phishing:</strong> Distributes malicious scripts, deceptive clickbait links, or social engineering lures.</li>
          </ul>
        </section>

        {/* Section 2 */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-accent" /> 2. Creator Responsibility &amp; Factuality
          </h2>
          <div className="rounded-xl border border-border bg-surface p-4 text-xs space-y-2">
            <p className="font-semibold text-foreground">
              Human-in-the-Loop Validation
            </p>
            <p>
              Generative AI can occasionally produce inaccuracies or hallucinated claims. As a creator, you retain full editorial control and responsibility over any script or video produced using our ideation features.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>You agree to verify factual, financial, medical, and legal claims before publishing them to public audiences.</li>
              <li>You agree to comply with third-party disclosure rules (such as YouTube&rsquo;s requirement to label realistic altered or synthetic content).</li>
            </ul>
          </div>
        </section>

        {/* Section 3 */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            3. Platform &amp; API Integrity
          </h2>
          <p className="text-xs">
            To ensure fair access and operational reliability for all creators, you agree NOT to:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>Automate bulk queries or scrape Virality through unauthorized bots, scripts, or proxies.</li>
            <li>Probe, scan, or test the vulnerability of our application, API endpoints, or database infrastructure without prior written authorization.</li>
            <li>Circumvent account limits, subscription gates, or trial restrictions.</li>
            <li>Attempt to reverse-engineer, decompile, or extract the underlying model weights and prompts.</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            4. Enforcement &amp; Reporting
          </h2>
          <p className="text-xs">
            We employ automated rate-limiting, safety filtering, and human review for flagged content. If you observe any misuse or abusive generation on the Virality network, please report it immediately:
          </p>
          <div className="rounded-xl border border-border bg-surface p-4 text-xs mt-2">
            <p className="font-bold text-foreground">Trust &amp; Safety Team</p>
            <p className="mt-1">Email: <a href="mailto:getintouch@viralityai.media" className="text-accent underline">getintouch@viralityai.media</a></p>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
}
